/**
 * Icon mapping for stat modifiers and actions
 */
import useGameStore from '@store/useGameStore';

/**
 * SVG color constants for effects (can't use CSS vars in SVG attributes)
 */
export const EFFECT_COLORS = {
  buff: '#22c55e',   // Green
  debuff: '#ef4444', // Red
  neutral: '#ffffff' // White
};

/**
 * Get the appropriate icon for an effect source
 * For now returns emoji strings, will be replaced with React components
 * @param {Object} source - The source object with type, id, and name
 * @returns {string} The emoji icon to display
 */
export const getSourceIcon = (source) => {
  // Handle null/undefined source
  if (!source) {
    console.warn('[getSourceIcon] No source provided');
    return '❓';
  }
  
  // Switch based on source type
  switch (source.type) {
    case 'unit': {
      // For units, we'll show a special emoji for now
      // TODO: Replace with actual UnitIcon component
      console.info(`[getSourceIcon] Unit source: ${source.name} (ID: ${source.id})`);
      return '👤';
    }
    
    case 'effect':
      console.warn(`[getSourceIcon] Effect source not implemented yet:`, source);
      return '❓';
    
    case 'ability':
      console.warn(`[getSourceIcon] Ability source not implemented yet:`, source);
      return '❓';
      
    case 'item':
      console.warn(`[getSourceIcon] Item source not implemented yet:`, source);
      return '❓';
      
    case 'environment':
      console.warn(`[getSourceIcon] Environment source not implemented yet:`, source);
      return '❓';

    case 'planned_action': {
      // Use action_id to get the action template directly (not ephemeral planned_action)
      const gameStore = useGameStore.getState();

      // NEW: Use action_id from source (persisted template ID)
      if (source.action_id) {
        const actionTemplate = gameStore.getActionById(source.action_id);
        if (actionTemplate) {
          return actionTemplate.icon || '⚡';
        }
        console.warn(`[getSourceIcon] Action template not found for action_id: ${source.action_id}`, source);
        return '⚡';
      }

      // FALLBACK: Old format (source.id is planned_action id - ephemeral)
      console.warn(`[getSourceIcon] No action_id in source, falling back to planned_action lookup (ephemeral):`, source);
      const plannedAction = gameStore.getHydratedPlannedActionById(source.id);
      if (plannedAction && plannedAction.action) {
        return plannedAction.action.icon || '⚡';
      }
      console.warn(`[getSourceIcon] Could not find action template or planned action:`, source);
      return '⚡';
    }

    default:
      console.warn(`[getSourceIcon] Unknown source type: ${source.type}`, source);
      console.trace();
      return '❓';
  }
};

// REMOVED: getModifierIcon - not used anywhere

export const getStatIcon = (statName) => {
  const statIconMap = {
    'strength': '⚔️',
    'armor': '🛡️',
    'movement': '👟',
    'agility': '🏃',
    'psi': '🧠',
    'will': '💪',
    'vision': '👁️',
    'max_ap': '⚡',
    'hp': '❤️',
    'max_hp': '❤️'
  };
  
  return statIconMap[statName] || '📊';
};

/**
 * Get Lucide icon name for a stat
 * @param {string} statName - The stat name
 * @returns {string} Lucide icon component name
 */
export const getStatIconName = (statName) => {
  const statIconMap = {
    'strength': 'Swords',
    'armor': 'Shield',
    'movement': 'Footprints',
    'agility': 'Rabbit',
    'psi': 'Brain',
    'will': 'ShieldCheck',
    'vision': 'Eye',
    'max_ap': 'Zap',
    'ap': 'Zap',
    'hp': 'Heart',
    'max_hp': 'Heart'
  };
  
  return statIconMap[statName] || 'Activity';
};

/**
 * Get appropriate emoji icon for an effect based on its slug
 * @param {string} effectSlug - The effect slug identifier
 * @returns {string} Emoji icon for the effect
 */
export const getEffectIcon = (effectSlug) => {
  if (!effectSlug) return '✨';
  
  // Normalize the slug to lowercase for comparison
  const slug = effectSlug.toLowerCase();
  
  const effectIconMap = {
    // Damage over time
    'poison': '☠️',
    'poison_apply': '☠️',
    'poison_damage': '☠️',
    'poison_cleanse': '✨',
    'acid': '🧪',
    'burn': '🔥',
    'bleed': '🩸',
    
    // Mental/Psi effects
    'mind_drain': '🧿',
    'mind_drain_apply': '🧿',
    'mind_drain_pulse': '🧿',
    'mind_drain_expire': '✨',
    'mind_control': '🧠',
    'confuse': '😵',
    'fear': '😨',
    
    // Defensive
    'shield': '🛡️',
    'shield_up': '🛡️',
    'defense_aura': '🛡️',
    'armor_break': '💔',
    
    // Energy/AP
    'energy_drain': '⚡',
    'defense_boost': '🛡️',
    'attack_boost': '⚔️',
    'speed_boost': '💨',
    'slow': '🐌',
    
    // Status effects
    'stun': '⭐',
    'stunned': '⚡',
    'freeze': '❄️',
    'blind': '👁️',
    'silence': '🤐',
    
    // Healing/Regen
    'heal': '❤️',
    'regeneration': '💚',
    
    // Death effects
    'death': '💀',
    'unit_death': '💀',
    'unit_poisoned_death': '☠️',
    'psichic_death': '🧿',
    'burn_death': '🔥',
    
    // Buff/Debuff generic
    'buffed': '⬆️',
    'debuffed': '⬇️',
    'shielded': '🛡️',
    
    // Default fallback
    'default': '✨'
  };
  
  // Check for exact match first
  if (effectIconMap[slug]) {
    return effectIconMap[slug];
  }
  
  // Check for partial matches (e.g., 'poison_tick' matches 'poison')
  for (const [key, icon] of Object.entries(effectIconMap)) {
    if (slug.includes(key)) {
      return icon;
    }
  }
  
  // Default sparkle for unknown effects
  return '✨';
};